import moment from 'moment-timezone';
import { v4 as uuidv4 } from 'uuid';
import { readEnvVariable } from '.';
import packageJSON from '../../package.json';
import { LogLevel } from '../constants/enums';
import { postLogToNewRelic } from '../services';
import { SOURCE } from './constants';
import { getLoggedInUserEmail, getTaskRouterAgentId } from './local-storage';
import seqIdSingletonInstance from './loggerSequenceId';

let logs: string[] = [];
let currentSessionId = '';
export const setLogSessionId = (sessionId: string) =>
  (currentSessionId = sessionId);

const serialize = (arg: any) => {
  switch (typeof arg) {
    case 'string':
      return arg;
    case 'boolean':
    case 'number':
      return arg.toString();
    default:
      return stringifyWithDepth(arg, 2, 0);
  }
};

// Courtesy of ChatGPT
/*
 * I need a function in typescript like JSON.stringify
 * but that only goes down to a depth of 2 and includes public properties.
 */
// Build a depth-limited *plain* copy: recursion returns real values (objects,
// arrays, primitives), replacing anything deeper than `depth` with '{...}'.
// Numbers stay numbers and strings stay strings, so the single JSON.stringify
// at the boundary below encodes exactly once. The previous implementation
// called JSON.stringify at every recursion level, so nested values were
// escaped repeatedly (e.g. `"seq":"123"`, `"email":"\"a@b.com\""`) and New
// Relic could not parse or pattern-match the payload.
const toDepthLimited = (
  obj: any,
  depth: number,
  currentDepth: number = 0
): any => {
  if (typeof obj === 'object' && obj !== null) {
    if (currentDepth >= depth) {
      return '{...}';
    }
    const objCopy: any = Array.isArray(obj) ? [] : {};
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        objCopy[key] = toDepthLimited(obj[key], depth, currentDepth + 1);
      }
    }
    return objCopy;
  }
  return obj;
};

const stringifyWithDepth = (
  obj: any,
  depth: number = 2,
  currentDepth: number = 0
): string => JSON.stringify(toDepthLimited(obj, depth, currentDepth));

// HITL-1282: Error's `name`, `message`, and `stack` are non-enumerable own
// properties in V8, so a plain `{ ...err }` spread silently drops them and
// New Relic only sees an empty payload. Convert Error instances to a plain
// object that preserves these fields (plus any enumerable properties the
// caller attached to the error) so the stack trace lands in logs.
const toPlainObjectForLogging = (value: object): object => {
  if (value instanceof Error) {
    // Spread first to capture any enumerable properties the caller attached,
    // then add the non-enumerable Error fields so they always make it into
    // logs regardless of whether the spread surfaced them.
    return {
      ...value,
      name: value.name,
      message: value.message,
      stack: value.stack,
    };
  }
  return value;
};

const loggerFn =
  (level: LogLevel) =>
  (...args: any[]) => {
    // typeof null === 'object' and typeof undefined !== 'string', so a naive
    // `typeof !== 'string'` check would route null/undefined into the
    // Object.entries branch below and throw "Cannot convert undefined or null
    // to object" — which would itself surface on the route-level ErrorPage
    // when ErrorPage's own `logger.error(error)` was called with a falsy
    // routeError. Restrict to real plain objects.
    const isLogAnObject =
      args[0] !== null && args[0] !== undefined && typeof args[0] === 'object';
    const sessionIdPrefix = currentSessionId ? `[${currentSessionId}]- ` : '';
    const objectPayload = isLogAnObject
      ? toPlainObjectForLogging(args[0])
      : null;

    //(1) Pushing logs for pagerduty ticket
    const logMessage = (
      objectPayload !== null
        ? Object.entries(objectPayload).map(
            ([key, value]) => `${key}: ${value}`
          )
        : args
    )
      .map(serialize)
      .join(' ');
    const timestamp = moment()
      .tz('America/Los_Angeles')
      .format('yyyy-MM-DD hh:mm:ss z');
    const processedLog = `${timestamp} - ${level} - ${sessionIdPrefix}${logMessage}`;
    logs.push(processedLog);

    // Prepare message content with session ID prefix for both New Relic and console
    // For objects: spread args[0] and add session prefix to message property if it exists
    // For strings: create { message: prefixed logMessage }
    const newRelicMessageContent =
      objectPayload !== null
        ? {
            ...objectPayload,
            ...('message' in objectPayload
              ? {
                  message: `${sessionIdPrefix}${
                    (objectPayload as { message?: unknown }).message ?? ''
                  }`,
                }
              : {}),
          }
        : { message: `${sessionIdPrefix}${logMessage}` };

    //(2) Post the logs to New Relic. The dev tiers (debug/log) stay on the
    // console + PagerDuty buffer only and are never shipped over the network
    // (B1: gate debug off the network). Building the payload — including the
    // depth-limited serialization — is skipped for them too.
    if (level !== LogLevel.debug && level !== LogLevel.log) {
      const newRelicMessagePayload = {
        loglevel: level,
        module: SOURCE,
        applicationUrl: window.location.href,
        applicationVersion: packageJSON.version,
        environment: readEnvVariable('DEPLOY_ENV'),
        browser: window.navigator.userAgent,
        message: stringifyWithDepth({
          data: {
            seq: seqIdSingletonInstance.incrementAndGetSeqId(),
            id: uuidv4(),
            agentId: getTaskRouterAgentId(),
            userEmail: getLoggedInUserEmail(),
            timestamp: new Date().toISOString(),
          },
          ...newRelicMessageContent,
        }),
      };
      postLogToNewRelic(newRelicMessagePayload);
    }

    // Use newRelicMessageContent for console output to maintain consistency
    // For objects: show the object with session prefix in message property
    // For strings: show just the prefixed message string
    return isLogAnObject
      ? console[level](newRelicMessageContent)
      : console[level](newRelicMessageContent.message);
  };

const logger = {
  debug: loggerFn(LogLevel.debug),
  info: loggerFn(LogLevel.info),
  warn: loggerFn(LogLevel.warn),
  error: loggerFn(LogLevel.error),
  log: loggerFn(LogLevel.log),
  getAllLogs: () => logs,
  clearLogs: () => {
    logs = [];
  },
};

export default logger;
